/**
 * WSS Validation Utilities
 * All validation logic for WSS check-in feature
 */

import _ from "lodash";
import { calculateAccurateRange } from "./gpsUtils";
import { ERROR_MESSAGES } from "./wssConstants";

/**
 * Validation 1: Check if location is enabled
 * @param {number} currentLatitude - Current user latitude
 * @param {number} currentLongitude - Current user longitude
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateLocationEnabled = (currentLatitude, currentLongitude) => {
  if (currentLatitude === 0 && currentLongitude === 0) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.LOCATION_DISABLED,
    };
  }
  return { isValid: true, error: "" };
};

/**
 * Validation 2: Check if required fields are selected
 * @param {string} wssName - Selected WSS name
 * @param {string} territoryId - Selected territory ID
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateRequiredFields = (wssName, territoryId) => {
  if (_.isEmpty(wssName) || _.isEmpty(territoryId)) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.REQUIRED_FIELDS,
    };
  }
  return { isValid: true, error: "" };
};

/**
 * Validation 3: Check if user is within accurate distance range
 * @param {number} distance - Calculated distance in meters
 * @param {number} masterAccuracy - Master WSS GPS accuracy
 * @param {number} currentAccuracy - Current user GPS accuracy
 * @returns {Object} { isValid: boolean, calculatedRange: number }
 */
export const validateDistance = (distance, masterAccuracy, currentAccuracy) => {
  const calculatedRange = calculateAccurateRange(masterAccuracy, currentAccuracy);
  const isValid = distance <= calculatedRange;
  
  return {
    isValid,
    calculatedRange,
    distance,
  };
};

/**
 * Validation 4: Check if WSS has GPS coordinates
 * @param {number} lat - WSS latitude
 * @param {number} long - WSS longitude
 * @returns {boolean} True if coordinates exist
 */
export const hasGPSCoordinates = (lat, long) => {
  return lat != null && long != null;
};

/**
 * Validation 5: Check MTP status and determine check-in flow
 * @param {string} mtpCheckIn - Monthly tour plan check-in status
 * @param {number} distance - Distance to WSS
 * @param {number} masterAccuracy - Master GPS accuracy
 * @param {number} currentAccuracy - Current GPS accuracy
 * @returns {Object} { shouldShowModal: boolean, isDirectCheckIn: boolean }
 */
export const validateMTPStatus = (mtpCheckIn, distance, masterAccuracy, currentAccuracy) => {
  const calculatedRange = calculateAccurateRange(masterAccuracy, currentAccuracy);
  
  if (mtpCheckIn === "Correct") {
    // MTP status is correct
    if (distance <= calculatedRange) {
      return { shouldShowModal: false, isDirectCheckIn: true };
    } else {
      return { shouldShowModal: false, isDirectCheckIn: false };
    }
  } else {
    // MTP status not correct or no GPS coordinates
    if (distance <= calculatedRange) {
      return { shouldShowModal: false, isDirectCheckIn: true };
    } else {
      return { shouldShowModal: true, isDirectCheckIn: false };
    }
  }
};

/**
 * Validation 6: Check if WSS selected for modal actions
 * @param {string} customerCode - WSS customer code
 * @param {string} actionType - Type of action (diary, sr, observations)
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateWssForAction = (customerCode, actionType) => {
  if (_.isEmpty(customerCode)) {
    let error = "";
    switch (actionType) {
      case "diary":
        error = ERROR_MESSAGES.SELECT_WSS_FOR_DIARY;
        break;
      case "sr":
        error = ERROR_MESSAGES.SELECT_WSS_FOR_SR;
        break;
      case "observations":
        error = ERROR_MESSAGES.SELECT_WSS_FOR_OBSERVATIONS;
        break;
      default:
        error = "Please select WSS";
    }
    return { isValid: false, error };
  }
  return { isValid: true, error: "" };
};

/**
 * Validation 7: Validate audit popup fields (HO Auditor only)
 * @param {string} retailCounter - Retail counter present (Yes/No)
 * @param {string} nonPerforming - Non-performing WSS (Yes/No)
 * @param {string} companyName - Company name (required if nonPerforming = Yes)
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateAuditFields = (retailCounter, nonPerforming, companyName) => {
  if (!retailCounter || !nonPerforming) {
    return {
      isValid: false,
      error: "Please fill all audit fields",
    };
  }
  
  if (nonPerforming === "Yes" && !companyName) {
    return {
      isValid: false,
      error: "Please enter company name for non-performing WSS",
    };
  }
  
  return { isValid: true, error: "" };
};
